import type {
  OfferConversionResponse,
  OrdersByDayResponse,
  RatingsByStayResponse,
} from "@/types/analytics-overview";
import type { TotalRevenueResponse } from "@/types/analytics-revenue";
import type { TenantOverviewResponse } from "@/types/analytics-tenant";

export interface OverviewAreaChartPoint {
  day: string;
  date: string;
  revenue: number | null;
  offerConversion: number | null;
  avgRating: number | null;
  ordersForStay: number | null;
}

interface OverviewAreaChartResponses {
  revenue?: TotalRevenueResponse;
  offerConversion?: OfferConversionResponse;
  ratingsByStay?: RatingsByStayResponse;
  ordersByDay?: OrdersByDayResponse;
}

function formatOverviewDayLabel(isoDay: string): string {
  const date = new Date(`${isoDay}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return isoDay;
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function createEmptyPoint(day: string): OverviewAreaChartPoint {
  return {
    day,
    date: formatOverviewDayLabel(day),
    revenue: null,
    offerConversion: null,
    avgRating: null,
    ordersForStay: null,
  };
}

function getOrCreatePoint(
  pointsByDay: Map<string, OverviewAreaChartPoint>,
  day: string,
): OverviewAreaChartPoint {
  const existingPoint = pointsByDay.get(day);
  if (existingPoint) {
    return existingPoint;
  }

  const point = createEmptyPoint(day);
  pointsByDay.set(day, point);
  return point;
}

function sortPointsByDay(
  pointsByDay: Map<string, OverviewAreaChartPoint>,
): OverviewAreaChartPoint[] {
  return Array.from(pointsByDay.values()).sort((left, right) =>
    left.day.localeCompare(right.day),
  );
}

export function mapOverviewMetricsToAreaChartData({
  revenue,
  offerConversion,
  ratingsByStay,
  ordersByDay,
}: OverviewAreaChartResponses): OverviewAreaChartPoint[] {
  const pointsByDay = new Map<string, OverviewAreaChartPoint>();
  const revenueDivisor = revenue?.amountDivisor || 1;

  for (const row of revenue?.series ?? []) {
    getOrCreatePoint(pointsByDay, row.day).revenue =
      row.byCurrency.reduce((sum, currencyRow) => sum + currencyRow.net, 0) /
      revenueDivisor;
  }

  const conversionTotalsByDay = new Map<
    string,
    { started: number; completed: number }
  >();
  for (const row of offerConversion?.series ?? []) {
    const totals = conversionTotalsByDay.get(row.day) ?? {
      started: 0,
      completed: 0,
    };
    totals.started += row.started;
    totals.completed += row.completed;
    conversionTotalsByDay.set(row.day, totals);
  }
  for (const [day, totals] of conversionTotalsByDay) {
    getOrCreatePoint(pointsByDay, day).offerConversion =
      totals.started > 0
        ? Math.round((totals.completed / totals.started) * 1000) / 10
        : null;
  }

  for (const row of ratingsByStay?.series ?? []) {
    getOrCreatePoint(pointsByDay, row.day).avgRating = row.avgRating;
  }

  for (const row of ordersByDay?.series ?? []) {
    getOrCreatePoint(pointsByDay, row.day).ordersForStay = row.orderCount;
  }

  return sortPointsByDay(pointsByDay);
}

export function mapTenantOverviewToAreaChartData(
  response: TenantOverviewResponse | undefined,
): OverviewAreaChartPoint[] {
  if (!response) {
    return [];
  }

  const pointsByDay = new Map<string, OverviewAreaChartPoint>();
  const revenueDivisor = response.amountDivisor || 1;

  for (const point of response.timeseries.revenue) {
    getOrCreatePoint(pointsByDay, point.day).revenue =
      point.value === null ? null : point.value / revenueDivisor;
  }
  for (const point of response.timeseries.offerConversion) {
    getOrCreatePoint(pointsByDay, point.day).offerConversion = point.value;
  }
  for (const point of response.timeseries.avgRating) {
    getOrCreatePoint(pointsByDay, point.day).avgRating = point.value;
  }
  for (const point of response.timeseries.ordersPerStay) {
    getOrCreatePoint(pointsByDay, point.day).ordersForStay = point.value;
  }

  return sortPointsByDay(pointsByDay);
}

export function mapTotalRevenueToAreaChartData(
  response: TotalRevenueResponse | undefined,
): OverviewAreaChartPoint[] {
  return mapOverviewMetricsToAreaChartData({ revenue: response });
}
